import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import PremiumFeatureModal from "../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";

export default function AccoladesTab({
  accolades,
  onOpenAdd,
  onDelete,
  hasSubscriptionAccess = true,
  hasUsedFreeTrial = false,
}) {
  const authUser = useAuthUser();
  const { user = {} } = authUser || {};
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const showFreeTrial =
    !hasSubscriptionAccess && user?.role === "athlete" && !hasUsedFreeTrial;

  const openSubscription = usePremiumUpsellRedirect(user);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const handleOpenAdd = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    onOpenAdd();
  };

  return (
    <div className="mt-4">
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Adding accolades is locked. Start a free trial or subscribe now to update your profile."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div
        className="float-right text-sm text-white border border-gray-300 p-2 rounded bg-blue-600 hover:bg-blue-800 cursor-pointer"
        onClick={handleOpenAdd}
      >
        <FontAwesomeIcon icon={faPlus} /> Add new Accolades
      </div>
      <h2 className="font-medium mb-4 uppercase">Accolades</h2>
      <div className="clear-both"></div>
      {Array.isArray(accolades) && accolades.length > 0 ? (
        <div className="mt-3 h-[400px] overflow-auto pr-4 -mr-4">
          {accolades.map((accolade, index) => (
            <div
              key={index}
              className="w-full bg-white border border-gray-200 rounded-md p-3 mb-2"
            >
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-5">
                  <p className="text-sm text-gray-500 mb-0">Description</p>
                  <p className="text-base font-normal text-gray-900">
                    {accolade.accoladeDescription}
                  </p>
                </div>
                <div className="col-span-6 md:col-span-4">
                  <p className="text-sm text-gray-500 mb-0">Classification</p>
                  <p className="text-base font-normal text-gray-900">
                    {accolade.accoladeClassification ?? "-"}
                  </p>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <p className="text-sm text-gray-500 mb-0">Year</p>
                  <p className="text-base font-normal text-gray-900">
                    {accolade.accoladeYear}
                  </p>
                </div>
                <div className="col-span-2 md:col-span-1 text-right pt-3">
                  <FontAwesomeIcon
                    icon={faTrash}
                    className="text-red-700 cursor-pointer"
                    onClick={() => onDelete(accolade.id)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          onClick={handleOpenAdd}
          className="hover:underline text-blue-700 cursor-pointer"
        >
          Add your first accolade here
        </div>
      )}
    </div>
  );
}
